import { z } from 'zod'

export const CONTACT_PROVIDERS = ['telegram', 'whatsapp', 'facebook', 'instagram', 'tiktok', 'viber', 'discord', 'github', 'signal', 'youtube', 'reddit', 'email', 'phone', 'other'] as const
export type ContactProvider = typeof CONTACT_PROVIDERS[number]
export const CONTACT_ICON_PRESETS = [...CONTACT_PROVIDERS, 'linkedin', 'x', 'threads', 'snapchat', 'pinterest', 'wechat', 'line', 'kakaotalk'] as const
export type ContactIconPreset = typeof CONTACT_ICON_PRESETS[number]
export const CONTACT_BRANDS: Record<ContactIconPreset, { label: string; color: string; src?: string }> = {
  telegram: { label: 'Telegram', color: '#0088cc', src: '/images/channels/telegram.svg' },
  whatsapp: { label: 'WhatsApp', color: '#128c4a', src: '/images/channels/whatsapp.svg' },
  facebook: { label: 'Messenger', color: '#0866ff', src: '/images/channels/facebook.svg' },
  instagram: { label: 'Instagram', color: '#c13584', src: '/images/channels/instagram.svg' },
  tiktok: { label: 'TikTok', color: '#181818', src: '/images/channels/tiktok.svg' },
  viber: { label: 'Viber', color: '#7360f2', src: '/images/channels/viber.svg' },
  discord: { label: 'Discord', color: '#5865f2', src: '/images/channels/discord.svg' },
  github: { label: 'GitHub', color: '#24292f', src: '/images/channels/github.svg' },
  signal: { label: 'Signal', color: '#3a76f0', src: '/images/channels/signal.svg' },
  youtube: { label: 'YouTube', color: '#ff0000', src: '/images/channels/youtube.svg' },
  reddit: { label: 'Reddit', color: '#ff4500', src: '/images/channels/reddit.svg' },
  linkedin: { label: 'LinkedIn', color: '#0A66C2', src: '/images/channels/linkedin.svg' },
  x: { label: 'X', color: '#171717', src: '/images/channels/x.svg' },
  threads: { label: 'Threads', color: '#171717', src: '/images/channels/threads.svg' },
  snapchat: { label: 'Snapchat', color: '#FFFC00', src: '/images/channels/snapchat.svg' },
  pinterest: { label: 'Pinterest', color: '#BD081C', src: '/images/channels/pinterest.svg' },
  wechat: { label: 'WeChat', color: '#07C160', src: '/images/channels/wechat.svg' },
  line: { label: 'LINE', color: '#00C300', src: '/images/channels/line.svg' },
  kakaotalk: { label: 'KakaoTalk', color: '#FFCD00', src: '/images/channels/kakaotalk.svg' },
  email: { label: 'Email', color: '#475569' },
  phone: { label: 'Phone', color: '#475569' },
  other: { label: 'Other link', color: '#475569' },
}
export const ContactProviderSchema = z.enum(CONTACT_PROVIDERS)
export const ContactIconSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('preset'), provider: z.enum(CONTACT_ICON_PRESETS) }).strict(),
  z.object({ kind: z.literal('upload'), assetId: z.string().uuid() }).strict(),
])
const LabelSchema = z.string().trim().min(1).refine(value => Array.from(value).length <= 12, 'Use at most 12 characters.')
export const CustomContactSchema = z.object({
  id: z.string().uuid(), provider: ContactProviderSchema, value: z.string().regex(/^[^\u0000-\u001f\u007f]*$/, 'Remove control characters from the destination.').trim().min(1).max(2048),
  label: LabelSchema, enabled: z.boolean(), icon: ContactIconSchema,
}).strict().superRefine((entry, ctx) => {
  try { buildContactUrl(entry.provider, entry.value) } catch (error) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value'], message: error instanceof Error ? error.message : 'Invalid destination.' })
  }
})
export const ContactConfigSchema = z.object({
  version: z.literal(1), enabled: z.boolean(),
  heading: z.string().trim().max(120, 'Use up to 120 characters.').optional(),
  connectedOverrides: z.array(z.object({ id: z.string().uuid(), enabled: z.boolean(), label: LabelSchema, provider: ContactProviderSchema, account: z.string().max(200) }).strict()).max(50),
  customLinks: z.array(CustomContactSchema).max(6, 'You can add at most six custom links.'),
}).strict().superRefine((config, ctx) => {
  const ids = [...config.connectedOverrides, ...config.customLinks].map(entry => entry.id)
  if (new Set(ids).size !== ids.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Channel IDs must be unique.' })

})
export type ContactConfig = z.infer<typeof ContactConfigSchema>
export type CustomContact = z.infer<typeof CustomContactSchema>
export type ContactIcon = z.infer<typeof ContactIconSchema>
export type PublicContact = { id: string; provider: ContactProvider; origin: 'connected' | 'custom'; label: string; url: string; icon: ContactIcon; imageUrl?: string }
export type ContactAccount = { id: string; provider: ContactProvider; account: string; status: string; url: string | null }
export const DEFAULT_CONTACT_CONFIG: ContactConfig = { version: 1, enabled: true, connectedOverrides: [], customLinks: [] }

export function buildContactUrl(provider: ContactProvider, input: string): string {
  const value = input.trim()
  if (/[\u0000-\u001f\u007f]/.test(input)) throw new Error('Remove control characters from the destination.')
  switch (provider) {
    case 'telegram': return `https://t.me/${handle(profileAccount(value, ['t.me']), /^[a-zA-Z0-9_]{5,32}$/)}`
    case 'instagram': return `https://ig.me/m/${handle(profileAccount(value, ['instagram.com', 'ig.me']), /^[a-zA-Z0-9._]{1,30}$/)}`
    case 'facebook': return `https://m.me/${handle(profileAccount(value, ['facebook.com', 'm.me']), /^[a-zA-Z0-9.]{1,100}$/)}`
    case 'tiktok': return `https://www.tiktok.com/@${handle(profileAccount(value, ['tiktok.com']), /^[a-zA-Z0-9._]{1,24}$/)}`
    case 'whatsapp': return `https://wa.me/${phone(value)}`
    case 'viber': return `viber://chat?number=%2B${phone(value)}`
    case 'phone': return `tel:+${phone(value)}`
    case 'email':
      if (!z.string().email().safeParse(value).success || /[?#]/.test(value)) throw new Error('Enter a valid email address.')
      return `mailto:${value}`
    case 'github':
      if (!value.startsWith('https://')) return `https://github.com/${handle(value, /^[a-zA-Z0-9-]{1,39}$/)}`
      return httpsUrl(value)
    case 'discord': case 'signal': case 'youtube': case 'reddit': case 'other': return httpsUrl(value)
  }
}

export function connectedContactDestination(provider: ContactProvider, account: string | null, externalId: string | null, metadata: Record<string, unknown>) {
  let value: string
  switch (provider) {
    case 'facebook': value = externalId ?? ''; break
    case 'whatsapp': value = typeof metadata.contact_phone_number === 'string' ? metadata.contact_phone_number : ''; break
    case 'instagram': case 'tiktok': value = typeof metadata.contact_username === 'string' ? metadata.contact_username : ''; break
    case 'telegram': value = account ?? ''; break
    default: return null
  }
  try { return buildContactUrl(provider, value) } catch { return null }
}

export function readContactConfig(value: unknown): ContactConfig {
  const parsed = ContactConfigSchema.safeParse(value)
  // Missing settings identify a pre-existing widget; never silently publish channels.
  return parsed.success ? parsed.data : { ...DEFAULT_CONTACT_CONFIG, enabled: false, connectedOverrides: [], customLinks: [] }
}

export function contactProviderForUrl(provider: ContactProvider, url: string): ContactProvider {
  if (provider !== 'other') return provider
  const host = new URL(url).hostname.replace(/^www\./, '')
  switch (host) {
    case 't.me': return 'telegram'
    case 'wa.me': return 'whatsapp'
    case 'm.me': case 'facebook.com': return 'facebook'
    case 'ig.me': case 'instagram.com': return 'instagram'
    case 'tiktok.com': return 'tiktok'
    case 'discord.gg': case 'discord.com': return 'discord'
    case 'github.com': return 'github'
    default: return provider
  }
}

export function resolveContactEntries(config: ContactConfig, accounts: ContactAccount[], assetUrls: Record<string, string> = {}): PublicContact[] {
  if (!config.enabled) return []
  const overrides = new Map(config.connectedOverrides.map(entry => [entry.id, entry]))
  const ordered = [...accounts].sort((a, b) => order(a.id) - order(b.id))
  const connected: PublicContact[] = ordered.filter(account => account.status === 'connected' && account.url && overrides.get(account.id)?.enabled !== false).map(account => ({
    id: account.id, provider: account.provider, origin: 'connected', label: overrides.get(account.id)?.label ?? CONTACT_BRANDS[account.provider].label,
    url: account.url!, icon: { kind: 'preset', provider: account.provider },
  }))
  const custom: PublicContact[] = config.customLinks.filter(entry => entry.enabled).flatMap(entry => {
    const url = buildContactUrl(entry.provider, entry.value)
    const provider = contactProviderForUrl(entry.provider, url)
    return [{ id: entry.id, provider, origin: 'custom' as const, label: entry.label, url, icon: entry.icon,
      imageUrl: entry.icon.kind === 'upload' ? assetUrls[entry.icon.assetId] : undefined }]
  })
  return [...connected, ...custom]
  function order(id: string) { const index = config.connectedOverrides.findIndex(entry => entry.id === id); return index < 0 ? Number.MAX_SAFE_INTEGER : index }
}

export function splitContactEntries(entries: PublicContact[]) {
  return entries.length <= 4 ? { visible: entries, overflow: [] } : { visible: entries.slice(0, 3), overflow: entries.slice(3) }
}

export function sanitizeContactPageUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 2048) return null
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? `${url.origin}${url.pathname}` : null } catch { return null }
}

function profileAccount(value: string, hosts: string[]) {
  if (!value.startsWith('https://')) return value
  const url = new URL(httpsUrl(value))
  if (!hosts.includes(url.hostname.replace(/^www\./, ''))) throw new Error('Use a profile URL for the selected channel.')
  let path = url.pathname.replace(/^\/|\/$/g, '')
  if (url.hostname === 'ig.me') path = path.replace(/^m\//, '')
  if (path.includes('/')) throw new Error('Use the account profile URL, rather than a post URL.')
  return path
}


function handle(value: string, pattern: RegExp) {
  const normalized = value.replace(/^@/, '')
  if (!pattern.test(normalized)) throw new Error('Enter a valid account username or ID.')
  return normalized
}
function phone(value: string) {
  const normalized = value.replace(/[\s().-]/g, '')
  if (!/^\+?[1-9]\d{7,14}$/.test(normalized)) throw new Error('Enter an international number with country code, such as +351912345678.')
  return normalized.replace(/^\+/, '')
}
function httpsUrl(value: string) {
  let url: URL
  try { url = new URL(value) } catch { throw new Error('Enter a complete HTTPS URL.') }
  if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Use an HTTPS URL without embedded credentials.')
  return url.href
}
